import React, { useCallback, useContext, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ProjectAPI from "@/src/api/ProjectAPI";
import { SegmentedFieldBase } from "@/src/components/form-fields/SegmentedField";
import { Button } from "@/src/components/ui/button";
import { Textarea } from "@/src/components/ui/textarea";
import ContentContext from "@/src/contexts/ContentContext";
import useAbility from "@/src/hooks/useAbility";
import useCopyToClipboard from "@/src/hooks/useCopyToClipboard";

import EmbedProfileSelect from "./EmbedProfileSelect";

import type { JSX } from "react";

import type { ContentContextValues } from "@/src/components/content/ContentTabs";
import type { AppState } from "@/src/reducers";

import type { EmbeddedItem, EmbedDialogBodyProps } from "./Embedded.types";

// Mounted only while the dialog is open, so the page header does not re-render on every media edit
export default function EmbedDialogBody({
  items,
  isVersion,
  popoverContainer,
}: EmbedDialogBodyProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const { canAccess } = useAbility();
  const copyToClipboard = useCopyToClipboard();
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );
  const { contentsWrite } = canAccess(orgaOptions);

  const [selectedProjectName, setSelectedProjectName] = useState<string>();

  const itemList: EmbeddedItem[] = Array.isArray(items)
    ? items
    : [{ iframeCode: items, projectName: "" }];
  const selectedItem = itemList.find(
    (item) => item.projectName === selectedProjectName,
  ) ??
    itemList[0] ?? { iframeCode: "", projectName: "" };

  const {
    data: embedProfileResponse,
    isSuccess: isOptionListReady,
    isError: isOptionListError,
    isFetching,
  } = useQuery(
    ProjectAPI.generateQuery({
      fn: ProjectAPI.getEmbedProfileOptions,
      params: [],
    }),
  );
  // The profile is saved with the page, like any other media field
  const { item: media, onItemUpdate } =
    useContext<ContentContextValues>(ContentContext);

  const handleEmbedProfileChange = (embedProfileUuid: string | null): void =>
    onItemUpdate({ ...media, embed_profile_uuid: embedProfileUuid });

  const handleCopy = useCallback((): void => {
    copyToClipboard({
      value: selectedItem.iframeCode,
      successMessage: t("embedCopied"),
    });
  }, [copyToClipboard, selectedItem.iframeCode, t]);

  return (
    <div className="flex flex-col gap-4">
      <EmbedProfileSelect
        description={isVersion ? t("embedProfile.versionHint") : undefined}
        disabled={!contentsWrite || !media.is_editable || !isOptionListReady}
        isError={isOptionListError}
        isLoading={!isOptionListReady && isFetching}
        onValueChange={handleEmbedProfileChange}
        optionList={embedProfileResponse?.data ?? []}
        popoverContainer={popoverContainer}
        value={media.embed_profile_uuid}
      />
      {itemList.length > 1 ? (
        <SegmentedFieldBase
          ariaLabel={t("embedProject")}
          onValueChange={setSelectedProjectName}
          value={selectedItem.projectName}
          options={itemList.map((item) => ({
            value: item.projectName,
            label: item.projectName,
          }))}
        />
      ) : null}
      <Textarea
        readOnly
        aria-label={t("embedCode")}
        className="bg-muted dark:bg-muted resize-none font-mono text-xs"
        rows={6}
        value={selectedItem.iframeCode}
      />
      <Button className="self-end" onClick={handleCopy} variant="outline">
        <Copy />
        {t("embedCopy")}
      </Button>
    </div>
  );
}
